import React from 'react';
import { BadgeCheck, Globe2, Leaf, Sparkles, Target } from '../components/Icons';

const values = [
  {
    title: 'Integrity in delivery',
    desc: 'We build trust with clear communication, transparent milestones, and predictable execution.',
    icon: BadgeCheck,
  },
  {
    title: 'Engineering-first decisions',
    desc: 'From design margins to component selection — we optimize for long-term yield and maintainability.',
    icon: Target,
  },
  {
    title: 'Sustainability that scales',
    desc: 'Projects that reduce emissions and create durable infrastructure for a cleaner grid.',
    icon: Leaf,
  },
  {
    title: 'Local roots, global mindset',
    desc: 'Pune-based execution with standards aligned to global best practices.',
    icon: Globe2,
  },
];

const milestones = [
  {
    title: 'Vision & foundation',
    desc: 'A Pune based team focused on accelerating the transition to renewable energy.',
  },
  {
    title: 'Operating portfolio established',
    desc: 'Two PV power stations operating 5 MW of clean generation on the grid.',
  },
  {
    title: 'Hybrid roadmap',
    desc: 'Expanding into storage and hybrid systems, with an eye on wind and hydro opportunities where they fit.',
  },
];

function Card({ children }) {
  return (
    <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-white/5 p-6 shadow-[0_20px_50px_-30px_rgba(0,0,0,0.9)] backdrop-blur">
      {children}
    </div>
  );
}

export default function About() {
  return (
    <div className="mx-auto max-w-6xl px-4">
      <section>
        <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-black/20 px-3 py-1 text-xs text-white/70 animate-fadeInUp">
          About Praash Green Energy
        </div>

        <div className="mt-4 grid gap-8 lg:grid-cols-12 lg:items-start">
          <div className="lg:col-span-7">
            <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl">
              Building renewable assets that perform today and for the next decade.
            </h1>
            <p className="mt-4 max-w-2xl text-base leading-relaxed text-white/80">
              Praash Green Energy is a Pune-based renewable infrastructure company focused on developing, building, owning, and operating clean energy assets. Our work began with solar power and is steadily expanding into energy storage and hybrid renewable systems.
              We take a long-term approach to every project, with a strong emphasis on safety, execution quality, and consistent operational performance.
            </p>
            <p className="mt-4 max-w-2xl text-base leading-relaxed text-white/80">
              At Praash Green Energy, clean energy isn&apos;t just our business, it&apos;s our purpose.
            </p>
          </div>

          <div className="lg:col-span-5">
            <Card>
              <div className="text-sm font-semibold">Mission</div>
              <p className="mt-2 text-sm leading-relaxed text-white/70">
                Develop renewable infrastructure that is easy to operate, resilient in the field, and designed for long-term performance.
              </p>
              <div className="mt-6 grid gap-3">
                <div className="rounded-2xl border border-white/10 bg-black/20 p-4">
                  <div className="text-xs text-white/60">Focus</div>
                  <div className="mt-1 text-sm">Origination • Development • Operations • Performance</div>
                </div>
                <div className="rounded-2xl border border-white/10 bg-black/20 p-4">
                  <div className="text-xs text-white/60">Principles</div>
                  <div className="mt-1 text-sm">Safety • Quality • Transparency</div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      <section className="mt-16">
        <div className="text-xs tracking-widest text-white/60">OUR VALUES</div>
        <h2 className="mt-2 text-2xl font-semibold tracking-tight sm:text-3xl">How we operate</h2>

        <div className="mt-6 grid gap-4 md:grid-cols-2">
          {values.map((v) => {
            const Icon = v.icon;
            return (
              <Card key={v.title}>
                <div className="flex items-start gap-4">
                  <div className="rounded-2xl border border-white/10 bg-white/5 p-3">
                    <Icon className="h-5 w-5 text-emerald-200" />
                  </div>
                  <div>
                    <div className="text-base font-semibold">{v.title}</div>
                    <p className="mt-1 text-sm leading-relaxed text-white/70">{v.desc}</p>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      </section>

      <section className="mt-16">
        <div className="text-xs tracking-widest text-white/60">MILESTONES</div>
        <h2 className="mt-2 text-2xl font-semibold tracking-tight sm:text-3xl">Progress you can track</h2>

        <div className="mt-6 grid gap-4">
          {milestones.map((m, idx) => (
            <div key={m.title} className="relative pl-10">
              <div className="absolute left-3 top-0 h-full w-px bg-white/10" />
              <div className="absolute left-0 top-0 flex h-6 w-6 items-center justify-center rounded-full border border-white/10 bg-white/5">
                <span className="text-xs text-white/80">{idx + 1}</span>
              </div>
              <Card>
                <div className="text-base font-semibold">{m.title}</div>
                <p className="mt-1 text-sm leading-relaxed text-white/70">{m.desc}</p>
              </Card>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
